import { useId } from 'react';
import { SparkleIcon } from '@phosphor-icons/react';
import {
  MotiveField,
  ObservationField,
  StaticSelectField,
} from '@voll-intelligence/intelligence-ui-kit';
import { policyViolationsOf } from '@voll-intelligence/intelligence-ui-kit/checkout';
import { hasSelectField, type CheckoutPrefill } from '@voll-intelligence/genui-contract';
import { useGenUiI18n } from '../../../../shared/ui/i18n.js';
import type { UseOneClickCheckout } from '../../core/use_one_click_checkout.js';
import { useCheckoutEnv } from '../../core/checkout_env.js';

// Os campos do pedido inteiro no card 1click — motivo, justificativa e
// observação —, que valem para todos os viajantes, e a marca do prefill.

/** Ocupa a linha inteira da `GRID` (uma coluna só no estreito). */
export const FULL_ROW = 'col-span-2 @max-[theme(--breakpoint-sm)]/checkout:col-span-1';

/** A marca do valor que veio preenchido (o ✦ do desenho). Muda: colada a um
 *  campo, quem a anuncia é o rótulo dele (`adornmentLabel`, «com sugestão da IA»,
 *  #6699); na nota do cabeçalho o fato já está em texto. */
export function PrefilledMark() {
  return <SparkleIcon size={14} weight="fill" className="text-sand-11" aria-hidden="true" />;
}

export type Catalog = NonNullable<UseOneClickCheckout['form']['travelers'][number]['form']>;

/** Só fora da política, e só quando o manifesto e o catálogo a têm. */
export function JustificationItem({
  flow,
  catalog,
  prefilled,
}: {
  readonly flow: UseOneClickCheckout;
  readonly catalog: Catalog;
  readonly prefilled: boolean;
}) {
  const { t } = useGenUiI18n();
  const { copy } = useCheckoutEnv();
  const { form } = flow;
  if (flow.withinPolicy || !hasSelectField(flow.fields, 'justification')) return null;
  if (catalog.justifications.length === 0) return null;
  const blocked = form.submitBlockers.includes('justification');
  // #5262: o PORQUÊ da justificativa obrigatória, quando a cotação o nomeia — a
  // regra e a janela exigida, para conferir com a política da empresa. Ela já diz
  // "Justificativa obrigatória", então toma o lugar do "Obrigatório" (com o mesmo
  // tom enquanto falta escolher) e fica, em aviso, depois de escolhida.
  const reasons = copy.traveler.outOfPolicyReasons(policyViolationsOf(flow.items));
  return (
    <StaticSelectField
      label={t('productCheckout.fieldJustification')}
      value={form.justificationId ?? ''}
      options={catalog.justifications.map((j) => ({ value: j.id, label: j.name }))}
      onChange={form.setJustificationId}
      placeholder={t('productCheckout.selectPlaceholder')}
      adornment={prefilled ? <PrefilledMark /> : undefined}
      adornmentLabel={t('productCheckout.oneClick.suggested')}
      required
      message={reasons ?? (blocked ? t('productCheckout.oneClick.required') : undefined)}
      messageTone={reasons && !blocked ? 'warning' : undefined}
    />
  );
}

/** O motivo do pedido — um só, para todos os viajantes. */
export function MotiveItem({
  flow,
  prefilled,
}: {
  readonly flow: UseOneClickCheckout;
  readonly prefilled: boolean;
}) {
  const { t } = useGenUiI18n();
  const copy = useCheckoutEnv().copy.fields;
  const catalog = flow.form.travelers[0]?.form;
  if (!catalog) return null;
  return (
    <MotiveField
      motives={catalog.motives}
      value={flow.form.motiveId}
      onChange={flow.form.setMotiveId}
      copy={copy.motive}
      adornment={prefilled ? <PrefilledMark /> : undefined}
      adornmentLabel={t('productCheckout.oneClick.suggested')}
      required
      message={
        flow.form.submitBlockers.includes('motive')
          ? t('productCheckout.oneClick.required')
          : undefined
      }
    />
  );
}

/** A observação para o aprovador (#6190), opcional e do pedido inteiro — a
 *  mesma do «Revisar dados», que a recebe pelo `handoffPrefill`. Aparece
 *  dentro e fora da política, sempre que o manifesto do produto a tem. */
export function ObservationItem({ flow }: { readonly flow: UseOneClickCheckout }) {
  const copy = useCheckoutEnv().copy.fields;
  const id = useId();
  const field = flow.fields.find((f) => f.kind === 'observation');
  if (field?.kind !== 'observation') return null;
  return (
    <div className={FULL_ROW}>
      <ObservationField
        id={id}
        copy={copy.observation}
        value={flow.form.observation}
        maxLength={field.maxLength}
        onChange={flow.form.setObservation}
      />
    </div>
  );
}

/** O que segue igual ao prefill — o ✦ some quando a pessoa troca o valor. */
export function prefilledMarks(
  form: UseOneClickCheckout['form'],
  prefill: CheckoutPrefill | undefined,
): { motive: boolean; justification: boolean } {
  return {
    motive: Boolean(form.motiveId) && form.motiveId === prefill?.motiveId,
    justification:
      Boolean(form.justificationId) && form.justificationId === prefill?.justificationId,
  };
}
